import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { useTranslation } from "react-i18next";
import I24Reset from "assets/icons/I24Reset";
import SearchStore from "stores/SearchStore";
import ButtonInline from "components/ButtonInline";
import CheckboxGroup from "components/CheckboxGroup";
import FormTitle from "components/Form/FormTitle";
import styleConst from "constants/style";

import {
  Buttons,
  ButtonWrapper,
  Container,
  Column,
  SubmitButton,
} from "./styles";

type Props = {
  onApply: () => void;
};

type Field = "workouts" | "recipes" | "resources";

interface Values {
  workouts: any[];
  recipes: any[];
  resources: any[];
}

interface StoreState {
  values: Values;
  readonly workoutOptions: { label: string; value: string }[];
  readonly recipeOptions: { label: string; value: string }[];
  readonly resourceOptions: { label: string; value: string }[];
  readonly fields: {
    title: string;
    field: Field;
    options: { label: string; value: string }[];
    value: any[];
  }[];
}

const FiltersForm: React.FC<Props> = observer(({ onApply }) => {
  const { t } = useTranslation();

  const state = useMemo(() => {
    const store: StoreState = observable({
      values: {
        workouts: [],
        recipes: [],
        resources: [],
      } as Values,

      get workoutOptions() {
        return SearchStore.workoutCategoryList.map(({ id, title }) => ({
          label: title,
          value: id.toString(),
        }));
      },

      get recipeOptions() {
        return SearchStore.foodCategoryList.map(({ id, title }) => ({
          label: title,
          value: id.toString(),
        }));
      },

      get resourceOptions() {
        return SearchStore.resourceCategoryList.map(({ id, title }) => ({
          label: title,
          value: id.toString(),
        }));
      },

      get fields() {
        return [
          {
            title: "Workouts",
            field: "workouts" as Field,
            options: store.workoutOptions,
            value: store.values.workouts,
          },
          {
            title: "Recipes",
            field: "recipes" as Field,
            options: store.recipeOptions,
            value: store.values.recipes,
          },
          {
            title: "Resources",
            field: "resources" as Field,
            options: store.resourceOptions,
            value: store.values.resources,
          },
        ];
      },
    });
    return store;
  }, []);

  useEffect(() => {
    if (SearchStore.currentFilters) {
      state.values = {
        workouts: [...(SearchStore.currentFilters.workouts || [])],
        recipes: [...(SearchStore.currentFilters.recipes || [])],
        resources: [...(SearchStore.currentFilters.resources || [])],
      };
    }
  }, [state]);

  const onSelectOption = (field: Field) => (items: any[]) => {
    state.values[field] = items;
  };

  const onResetFilters = () => {
    state.values = {
      workouts: [],
      recipes: [],
      resources: [],
    };

    SearchStore.setFilters(state.values);
    onApply();
  };

  const onShowResults = () => {
    SearchStore.setFilters(state.values);
    onApply();
  };

  return (
    <>
      <FormTitle title={t("titles.filter")} />
      <Container>
        {state.fields
          .filter(({ options }: any) => options.length)
          .map((item: any) => (
            <Column key={item.field}>
              <CheckboxGroup
                title={item.title}
                value={item.value}
                onSelectOption={onSelectOption(item.field)}
                options={item.options}
              />
            </Column>
          ))}
      </Container>
      <Buttons>
        <ButtonWrapper>
          <ButtonInline
            text={t("actions.reset-all")}
            onPress={onResetFilters}
            fontSize={16}
            fontWeight={500}
            iconLeft={<I24Reset color={styleConst.colors.primary} />}
          />
        </ButtonWrapper>
        <ButtonWrapper>
          <SubmitButton
            height={50}
            text={t("actions.show-results")}
            onClick={onShowResults}
          />
        </ButtonWrapper>
      </Buttons>
    </>
  );
});

export default FiltersForm;

